import { z } from 'zod'
import { jstDayStart } from './activity-presentation'
import type { ContentHistory } from './content-history-types'

const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`)
    return (
      Number.isFinite(date.getTime()) &&
      date.toISOString().slice(0, 10) === value
    )
  }, 'Enter a valid date.')
export const historyWindow = z
  .object({ from: day, to: day })
  .superRefine((value, ctx) => {
    const days =
      (Date.parse(jstDayStart(value.to)) -
        Date.parse(jstDayStart(value.from))) /
      86400000
    if (!(days > 0 && days <= 31))
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: 'Choose an end after the start, within 31 days.',
      })
  })
export const historyIdentity = z.guid()
export function defaultHistoryDays() {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
  return {
    from: new Date(Date.parse(`${today}T00:00:00Z`) - 7 * 86400000)
      .toISOString()
      .slice(0, 10),
    to: today,
  }
}
export const HISTORY_REASONS: Record<string, string> = {
  before_catalog_baseline:
    'The requested window starts before recorded catalog history.',
  status_evidence_incomplete:
    'Some content status cannot be established from the retained evidence.',
  parent_evidence_incomplete:
    'Some availability cannot be established from the content and its parents.',
  session_start_evidence_missing:
    'Some calls lack evidence of when the voice session started.',
  call_archive_not_loaded: 'Archived or deleted call evidence is not included.',
  prompt_use_not_recorded:
    'Calls do not record which individual tutor prompt was used.',
  call_program_context_missing:
    'Some calls lack the Program context needed for this count.',
  audit_query_limit:
    'This content exceeds the history retrieval limit; a complete answer is unavailable.',
  future_window: 'Part of the requested window is in the future.',
  evidence_missing: 'Historical evidence missing',
  status_unknown: 'Status unknown',
  context_evidence_missing: 'Program history missing',
  invalid_hierarchy: 'Invalid recorded hierarchy',
  program_context_required: 'Program context required',
  parent_reference_missing: 'Recorded parent missing',
  diagnostic_not_selected: 'Not the Program’s selected diagnostic',
  eligibility_unknown: 'Level eligibility unknown',
  level_ineligible: 'Unavailable for the selected learner level',
  draft: 'Draft',
  retired: 'Retired',
}
export function historyReason(reason: string) {
  return HISTORY_REASONS[reason] ?? `Unrecognized evidence reason: ${reason}`
}

/** Merge two complete interval streams, preserving changes on either axis. */
export function historyRows(data: ContentHistory) {
  const rows: {
    start: string
    end: string
    status: ContentHistory['statusPeriods'][number]['status']
    inheritedStatus: ContentHistory['statusPeriods'][number]['inheritedStatus']
    statusKnown: boolean
    available: boolean | null
    blockers: ContentHistory['availabilityPeriods'][number]['blockers']
  }[] = []
  let i = 0,
    j = 0
  while (i < data.statusPeriods.length && j < data.availabilityPeriods.length) {
    const status = data.statusPeriods[i]!,
      availability = data.availabilityPeriods[j]!
    const start =
      Date.parse(status.start) >= Date.parse(availability.start)
        ? status.start
        : availability.start
    const end =
      Date.parse(status.end) <= Date.parse(availability.end)
        ? status.end
        : availability.end
    if (Date.parse(start) < Date.parse(end))
      rows.push({
        start,
        end,
        status: status.status,
        inheritedStatus: status.inheritedStatus,
        statusKnown: status.known,
        available: availability.available,
        blockers: availability.blockers,
      })
    if (Date.parse(status.end) <= Date.parse(availability.end)) i++
    if (Date.parse(availability.end) <= Date.parse(status.end)) j++
  }
  return rows
}


export function contentHistoryDetailReturn(params: URLSearchParams): string | undefined {
  const value = params.get('detail') ?? ''
  return /^\/admin\/content\/(programs|levels|themes|scenarios|diagnostics|tutors)\/[^/?#\\]+(?:\?[^#]*)?$/.test(value) ? value : undefined
}
